import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Papa } from 'ngx-papaparse';
import { BehaviorSubject } from 'rxjs';
import { writeLog } from 'src/app/shared/misc/logs';
import { MiscService } from 'src/app/shared/service/misc.service';
import { VCardService } from 'src/app/shared/service/vcard.service';
import * as XLSX from 'xlsx';
import { Contact, ContactManagerData, Group } from '../../../model';
import { AlertService } from '../../../service/alert.service';

@Component({
  selector: 'app-vcard-import',
  templateUrl: './vcard-import.component.html',
  styleUrls: ['./vcard-import.component.scss']
})
export class VcardImportComponent implements OnInit {
  fileUploaded = false;
  fileProcessed = false;
  groups: Group[] = [];
  selectedGroup: Group;
  contacts: Contact[] = [];
  parsedData = []; // Holds the full parsed data (including potential headers)
  parsedRowData = []; // Holds the row used for column selection (usually the first row)
  files: any = [];
  valuesHolder: Contact = new Contact();
  skipColumnHeaders: boolean = false; // Flag to indicate if the first row should be skipped as headers
  skippedContacts = 0;
  supportedFiles = '.vcf, .xls, .xlsx, .csv, .txt';
  uploadFileType = ''; // Stores the detected type of the uploaded file
  selectedTxtDelimiter = 0;
  parsedTxtData = '';
  txtDelimiters = ['Comma', 'New Line'];

  private fileReaderSource = new BehaviorSubject<{ file: File, result: string | ArrayBuffer }>(null);

  constructor(
    private papaService: Papa,
    private alert: AlertService,
    private vcardService: VCardService,
    public miscService: MiscService,
    public dialogRef: MatDialogRef<VcardImportComponent, Contact[]>,
    @Inject(MAT_DIALOG_DATA) public data: ContactManagerData,
  ) {}

  ngOnInit() {
    // Set contact group to null or selectedGroup from injected data
    this.selectedGroup = this.data.selectedGroup;

    // Subscribe to group updates
    this.data.groups.subscribe(
      (res) => {
        this.groups = res;

        // If no group is pre-selected, default to the first group in the list
        if (!this.selectedGroup && this.groups.length > 0) {
          this.selectedGroup = this.groups[0];
        }
      }
    );

    // Listen to file contents read from the FileReader
    this.fileReaderSource.asObservable().subscribe(res => {
      writeLog('FILE RESPONSE', res);
      if (res != null) {
        this.onFileContentsRead(res.file, res.result);
      }
    });

    // Initialize upload state
    this.resetUpload();
  }

  /**
   * Resets all component properties to their initial state, clearing any previous upload data.
   */
  resetUpload() {
    this.valuesHolder = {
      surname: '-1',
      othername: '-1',
      mobile: '-1',
      custom_1: '-1',
      custom_2: '-1',
      custom_3: '-1',
      custom_4: '-1',
      group_id: -1,
    };
    this.fileProcessed = false;
    this.fileUploaded = false;
    this.skipColumnHeaders = false;
    this.skippedContacts = 0;
    this.contacts = [];
    this.files = [];
    this.parsedData = [];
    this.parsedRowData = [];
    this.uploadFileType = '';
    this.selectedTxtDelimiter = 0;
    this.parsedTxtData = '';
  }

  /**
   * Handles files dropped onto the component's drop area.
   * @param event The array of files dropped.
   */
  onDropFile(event: File[]) {
    for (let i = 0; i < event.length; i++) {
      const element = event[i];
      this.onUploadStart(element);
    }
  }

  /**
   * Handles files chosen via the file input element.
   * @param event The event object from the file input.
   */
  onChooseFile(event: any) {
    if (event.srcElement.files.length && event.srcElement.files[0]) {
      const file = event.srcElement.files[0];
      this.onUploadStart(file);
    }
  }

  /**
   * Initiates the file reading process based on file type.
   * @param file The file to be uploaded and read.
   */
  onUploadStart(file: File) {
    writeLog('File selected for upload:', file);
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      // Push the file and its contents to the BehaviorSubject
      this.fileReaderSource.next({
        file: file,
        result: e.target.result
      });
    };

    // Determine how to read the file based on its extension
    if (this.shouldReadAsBinary(file.name)) {
      reader.readAsBinaryString(file);
    } else {
      reader.readAsText(file);
    }
  }

  /**
   * Processes the file contents after they have been read by FileReader.
   * Directs the content to the appropriate handler based on file extension.
   * @param file The uploaded file object.
   * @param contents The content of the file, either as string or ArrayBuffer.
   */
  onFileContentsRead(file: File, contents: string | ArrayBuffer) {
    writeLog('Processing file:', file.name);
    switch (this.getFileExtension(file.name)) {
      case 'vcf':
        this.onUploadVcard(contents as string);
        break;

      case 'csv':
        this.onUploadCSV(contents as string);
        break;

      case 'xls':
      case 'xlsx':
        this.onUploadXLSX(contents as string);
        break;

      case 'txt':
        this.onUploadTXT(contents as string);
        break;

      default:
        writeLog('File type not supported:', file.name);
        this.alert.alert({
          message: `File type is not supported. Please upload a supported file (${this.supportedFiles}).`
        });
        this.resetUpload(); // Reset upload if file type is not supported
        break;
    }
  }

  /**
   * Handles the upload and parsing of VCard (.vcf) files.
   * Parses the vCard data and prepares it for field mapping.
   * @param contents The string content of the VCF file.
   */
  onUploadVcard(contents: string) {
    const vcard = this.vcardService.parse(contents);

    const results = [];
    // Manually push standard headers for VCF files, as the service returns parsed objects
    results.push(['Name', 'Phone']);
    vcard.forEach((item) => {
      // Map vCard properties to a flat array for display
      results.push([item.fn || '', this.miscService.trimMobile(item.tel) || '']);
    });

    writeLog('Parsed VCard:', vcard);
    writeLog('VCard results prepared for display:', results);

    if (!results.length || (results.length === 1 && results[0].length === 0)) {
      this.alert.alert({
        message: 'The VCard(.vcf) file appears to be empty or incompatible. Please check the file and try again.'
      });
      this.resetUpload();
      return;
    }

    // Set parsed data and row data for VCF
    this.parsedData = results;
    this.parsedRowData = results[0]; // Set parsedRowData to the manually added headers
    this.fileUploaded = true;
    this.uploadFileType = 'vcf'; // Set uploadFileType to 'vcf'
    this.skipColumnHeaders = true; // Always skip headers for VCF as they are manually created
  }

  /**
   * Extracts the file extension from a given filename.
   * @param filename The name of the file.
   * @returns The file extension (e.g., 'csv', 'xlsx').
   */
  getFileExtension(filename: string): string {
    const parts = filename.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  }

  /**
   * Determines if a file should be read as a binary string (for Excel files).
   * @param filename The name of the file.
   * @returns True if the file is an XLS or XLSX, false otherwise.
   */
  shouldReadAsBinary(filename: string): boolean {
    const ext = this.getFileExtension(filename);
    return ext === 'xls' || ext === 'xlsx';
  }

  /**
   * Handles the upload and parsing of CSV files using PapaParse.
   * Prompts the user about the presence of headers.
   * @param csvData The string content of the CSV file.
   */
  onUploadCSV(csvData: string) {
    writeLog('Parsing CSV contents...');
    this.papaService.parse(csvData, {
      complete: (results) => {
        writeLog('PapaParse results:', results);

        // if (results.errors.length > 0) {
        //   this.alert.alert({
        //     message: 'The CSV file appears to contain invalid data or is corrupted. Please check the file and try again.'
        //   });
        //   this.resetUpload();
        //   return;
        // }

        if (results.data.length === 0 || (results.data.length === 1 && results.data[0].length === 0)) {
          this.alert.alert({
            message: 'The CSV file appears to be either empty or contains invalid data. Please check the file and try again.'
          });
          this.resetUpload();
          return;
        }

        // Store all parsed data
        this.parsedData = results.data;
        this.fileUploaded = true;
        this.uploadFileType = 'csv';

        // Prompt user about column headers
        this.alert.confirm({
          title: 'Confirm Headers',
          message: `The file contains ${this.parsedData.length} rows. Does the file contain headers on the first row?`,
          confirmText: 'YES',
          cancelText: 'NO',
          callback: (hasHeaders: boolean) => {
            // parsedRowData should always reflect the first row for selection regardless of `skipColumnHeaders`
            this.parsedRowData = JSON.parse(JSON.stringify(this.parsedData[0]));
            this.skipColumnHeaders = hasHeaders; // Set flag based on user's confirmation
          }
        });
      },
      error: (error: any) => {
        writeLog('PapaParse error:', error);
        this.alert.alert({
          message: 'An error occurred during CSV parsing. Please ensure the file is a valid CSV.'
        });
        this.resetUpload();
      }
    });
  }

  /**
   * Handles the upload and parsing of XLSX/XLS (Excel) files using SheetJS.
   * Prompts the user about the presence of headers.
   * @param fileData The binary string content of the Excel file.
   */
  onUploadXLSX(fileData: string) {
    writeLog('Parsing XLSX/XLS contents...');
    try {
      /* read workbook */
      const wb: XLSX.WorkBook = XLSX.read(fileData, { type: 'binary' });
      writeLog('Workbook object:', wb);

      if (!wb.SheetNames.length || !wb.Sheets) {
        this.alert.alert({
          message: 'The Excel file appears to contain no sheets. Please check the file and try again.'
        });
        this.resetUpload();
        return;
      }

      const wsname: string = wb.SheetNames[0]; // Get the first sheet name
      const ws: XLSX.WorkSheet = wb.Sheets[wsname]; // Get the first worksheet

      /* get excel data as array of arrays */
      const data = (XLSX.utils.sheet_to_json(ws, { header: 1 })) as any[][];
      writeLog('Excel data:', data);

      if (!data || data.length === 0 || (data.length === 1 && data[0].length === 0)) {
        this.alert.alert({
          message: 'The Excel file appears to be empty. Please check the file and try again.'
        });
        this.resetUpload();
        return;
      }

      // Store all parsed data
      this.parsedData = data;
      this.fileUploaded = true;
      this.uploadFileType = 'csv'; // Treat XLSX similarly to CSV for field mapping

      // Prompt user about column headers
      this.alert.confirm({
        title: 'Confirm Headers',
        message: `The file contains ${this.parsedData.length} rows. Does the file contain headers on the first row?`,
        confirmText: 'YES',
        cancelText: 'NO',
        callback: (hasHeaders: boolean) => {
          // parsedRowData should always reflect the first row for selection regardless of `skipColumnHeaders`
          this.parsedRowData = JSON.parse(JSON.stringify(this.parsedData[0]));
          this.skipColumnHeaders = hasHeaders; // Set flag based on user's confirmation
        }
      });

    } catch (error) {
      writeLog('XLSX parsing error:', error);
      this.alert.alert({
        message: 'An error occurred during Excel parsing. Please ensure the file is a valid XLS/XLSX file.'
      });
      this.resetUpload();
    }
  }

  /**
   * Handles the upload and initial storage of TXT file contents.
   * The actual parsing and processing occur after delimiter confirmation.
   * @param txtData The string content of the TXT file.
   */
  onUploadTXT(txtData: string) {
    writeLog('TXT file data received:', txtData);

    if (txtData.trim().length === 0) {
      this.alert.alert({
        message: 'The TXT file appears to be empty. Please check the file and try again.'
      });
      this.resetUpload();
      return;
    }

    this.parsedTxtData = txtData;
    this.fileUploaded = true;
    this.uploadFileType = 'txt';
  }

  /**
   * Confirms the delimiter for TXT file processing and then processes the contacts.
   * This method is called after the user selects a delimiter for the TXT file.
   */
  onConfirmTXTProcess() {
    writeLog('TXT delimiter selected:', this.selectedTxtDelimiter);

    let delimiter = ',';
    switch (this.selectedTxtDelimiter) {
      case 0: // Comma
        delimiter = ',';
        break;
      case 1: // New Line
        delimiter = '\n';
        break;
      default:
        delimiter = ','; // Default to comma
        break;
    }

    const arr = this.parsedTxtData.split(delimiter);
    writeLog('TXT data split by delimiter:', arr);

    this.contacts = []; // Clear existing contacts before reprocessing
    this.skippedContacts = 0; // Reset skipped contacts

    arr.forEach(item => {
      if (item.trim() === '') { // Skip empty lines/items
        return;
      }
      const newContact = new Contact();
      newContact.group_id = this.selectedGroup ? this.selectedGroup.id : 0;
      newContact.mobile = this.miscService.trimMobile(item); // Assume each item is a mobile number

      if (!newContact.mobile || !newContact.mobile.length) {
        this.skippedContacts++;
        return;
      }

      this.contacts.push(newContact);
    });

    if (!this.contacts.length) {
      this.alert.alert({
        message: 'Sorry, no valid contacts were found after processing the TXT file with the selected delimiter. Make sure the file contains valid data, then try the upload again.'
      });
      this.resetUpload();
      return;
    }

    this.fileProcessed = true;
  }

  /**
   * Removes a recipient (contact) from the list of contacts to be imported.
   * @param index The index of the contact to remove.
   */
  removeRecipient(index: number) {
    if (index >= 0 && index < this.contacts.length) {
      this.contacts.splice(index, 1);
    }
  }

  /**
   * Sets the selected group for all contacts in the current list.
   */
  setGroupForContacts() {
    if (this.selectedGroup) {
      for (let i = 0; i < this.contacts.length; i++) {
        this.contacts[i].group_id = this.selectedGroup.id;
      }
    } else {
      writeLog('No group selected to assign to contacts.');
    }
  }

  /**
   * Confirms the field mappings based on the uploaded file type.
   * Directs to the appropriate processing method.
   */
  onConfirmFields() {
    switch (this.uploadFileType) {
      case 'csv': // This case also handles XLSX and VCF as they are processed similarly
        this.onConfirmCSVProcess();
        break;

      case 'vcf': // Although processed like CSV for mapping, keep distinct for clarity if needed
        this.onConfirmCSVProcess(); // Reuse CSV processing for VCF due to simplified array structure
        break;

      case 'txt':
        this.onConfirmTXTProcess();
        break;

      default:
        this.alert.alert({
          message: 'Unknown file type for field confirmation. Please re-upload your file.'
        });
        break;
    }
  }

  /**
   * Processes the parsed CSV/XLSX/VCF data based on user-selected field mappings.
   * Validates mobile numbers and constructs the final contact list.
   */
  onConfirmCSVProcess() {
    writeLog('Selected field mappings:', this.valuesHolder);

    // Validate if at least the mobile number column is selected
    if (+this.valuesHolder.mobile < 0) {
      this.alert.alert({
        message: 'Please select a column for the Mobile number field. It is a required field.'
      });
      return;
    }

    this.contacts = []; // Clear existing contacts
    this.skippedContacts = 0; // Reset skipped count

    // Loop through the parsed data to create contact objects
    for (let i = 0; i < this.parsedData.length; i++) {
      // Skip the first row if it contains headers and the flag is set
      if (i === 0 && this.skipColumnHeaders) {
        continue;
      }

      const newContact = new Contact();
      newContact.group_id = this.selectedGroup ? this.selectedGroup.id : 0;

      // Assign surname if a column is mapped and data exists
      if (+this.valuesHolder.surname >= 0 && this.parsedData[i][this.valuesHolder.surname]) {
        newContact.surname = String(this.parsedData[i][this.valuesHolder.surname]).trim();
      }

      // Assign mobile number (required field)
      const rawMobile = this.parsedData[i][this.valuesHolder.mobile];
      newContact.mobile = this.miscService.trimMobile(rawMobile ? String(rawMobile) : '');

      // Check if mobile number is valid after trimming
      if (!newContact.mobile.length) {
        this.skippedContacts++;
        continue; // Skip contact if mobile is empty or invalid
      }

      // Assign other fields if mapped and data exists
      if (+this.valuesHolder.othername >= 0 && this.parsedData[i][this.valuesHolder.othername]) {
        newContact.othername = String(this.parsedData[i][this.valuesHolder.othername]).trim();
      }
      if (+this.valuesHolder.custom_1 >= 0 && this.parsedData[i][this.valuesHolder.custom_1]) {
        newContact.custom_1 = String(this.parsedData[i][this.valuesHolder.custom_1]).trim();
      }
      if (+this.valuesHolder.custom_2 >= 0 && this.parsedData[i][this.valuesHolder.custom_2]) {
        newContact.custom_2 = String(this.parsedData[i][this.valuesHolder.custom_2]).trim();
      }
      if (+this.valuesHolder.custom_3 >= 0 && this.parsedData[i][this.valuesHolder.custom_3]) {
        newContact.custom_3 = String(this.parsedData[i][this.valuesHolder.custom_3]).trim();
      }
      if (+this.valuesHolder.custom_4 >= 0 && this.parsedData[i][this.valuesHolder.custom_4]) {
        newContact.custom_4 = String(this.parsedData[i][this.valuesHolder.custom_4]).trim();
      }

      this.contacts.push(newContact);
    }

    writeLog('Final contacts list for import:', this.contacts);

    if (!this.contacts.length) {
      this.alert.alert({
        message: 'No valid contacts could be extracted with the selected column mappings. Please ensure the selected MOBILE column contains valid numbers and try again.'
      });
      // Do not resetUpload() here, allow user to adjust mappings
      return;
    }

    this.fileProcessed = true; // Mark file as processed
  }

  /**
   * Provides sample field values for display in the column mapping dropdowns.
   * @param index The column index for which to retrieve sample values.
   * @returns A string showing sample values or a message if no values are found.
   */
  getFieldValues(index: number): string {
    if (index === -1) {
      return ''; // Not yet selected
    }

    const arr: string[] = [];
    let startRow = 0;
    // Adjust start row if headers are skipped
    if (this.skipColumnHeaders && this.parsedData.length > 0) {
      startRow = 1;
    }

    // Iterate through data to collect up to 5 non-empty sample values
    for (let i = startRow; i < this.parsedData.length && arr.length < 5; i++) {
      const element = this.parsedData[i];
      if (element && element[index] !== undefined && element[index] !== null && String(element[index]).trim() !== '') {
        arr.push(String(element[index]).trim());
      }
    }

    if (arr.length === 0) {
      return 'No values were found under this column!';
    }
    return 'Found: ' + arr.join(', ');
  }

  /**
   * Finalizes the import process by closing the dialog and returning the processed contacts.
   */
  onImport() {
    if (!this.contacts.length) {
      this.alert.alert({
        message: 'No contacts are ready for import. Please upload and process a file first.'
      });
      return;
    }

    this.dialogRef.close(this.contacts);
  }
}
